export enum PotatoType {
  Frietaardappelen = 'Frietaardappelen',
  Tafelaardappelen = 'Tafelaardappelen',
}
export type TgaInputType = { key: string; selected: boolean }[] | string | number | null;

export interface QuestionDependency {
  questionKey: string;
  questionVisibleForValues: string[];
}

export interface ExternalPropertyDependency {
  propertyKey: string;
  questionVisibleForValues: string[];
}
export interface GreenManureCropOption {
  cropId: number;
  otherGreenManureUsed: boolean;
}
export interface FieldSensitivity {
  hasZwarteSpikkelPressure: boolean;
  hasSclerotiniePressure: boolean;
  hasRhizoctoniaPressure: boolean;
  hasGewoneSchurftPressure: boolean;
  hasRitnaalden: boolean;
  expectedStorageOnCropField: boolean;
  hasAaltjesDamage: boolean;
  hasTabaksratelvirus: boolean;
  hasSnails: boolean;
}
export interface Structure {
  hasStructuralDeterioration: boolean;
  structuralSpoilageSource: string;
}
export abstract class TGAQuestion<T> {
  key: string;
  label: string;
  value?: T;
  options?: { key: string; value: string }[];
  order: number;
  controlType = '';
  placeholder?: string;
  description?: string;
  forCropIds?: number[];
  required: boolean;
  isVisible?: boolean;
  isDisabled: boolean;
  hasInfoIcon?: boolean;
  dependsOnQuestion?: QuestionDependency;
  dependsOnExternalProperty?: ExternalPropertyDependency;
  cropFieldSpecificResults?: boolean;

  constructor(options: {
    key: string;
    label: string;
    value?: T;
    options?: { key: string; value: string }[];
    order?: number;
    forCropIds?: number[];
    required?: boolean;
    placeholder?: string;
    description?: string;
    isVisible?: boolean;
    isDisabled?: boolean;
    hasInfoIcon?: boolean;
    dependsOnQuestion?: QuestionDependency;
    dependsOnExternalProperty?: ExternalPropertyDependency;
    cropFieldSpecificResults?: boolean;
  }) {
    this.value = options.value;
    this.key = options.key;
    this.label = options.label;
    this.required = options.required || false;
    this.order = options.order ?? 1;
    this.options = options.options;
    this.forCropIds = options.forCropIds || [];
    this.placeholder = options.placeholder;
    this.description = options.description;
    this.isVisible =
      options.isVisible ?? (options.dependsOnQuestion == null && options.dependsOnExternalProperty == null);
    this.isDisabled = options.isDisabled || false;
    this.hasInfoIcon = options.hasInfoIcon;
    this.dependsOnQuestion = options.dependsOnQuestion;
    this.dependsOnExternalProperty = options.dependsOnExternalProperty;
    this.cropFieldSpecificResults = options.cropFieldSpecificResults;
  }
}

export class TGARadioButtonQuestion extends TGAQuestion<string> {
  static readonly radioControlType = 'radio-button';

  controlType = TGARadioButtonQuestion.radioControlType;

  constructor(options: {
    key: string;
    label: string;
    value?: string;
    options: { key: string; value: string }[];
    order?: number;
    forCropIds?: number[];
    required?: boolean;
    placeholder?: string;
    isVisible?: boolean;
    dependsOnQuestion?: QuestionDependency;
    dependsOnExternalProperty?: ExternalPropertyDependency;
    cropFieldSpecificResults?: boolean;
  }) {
    super(options);

    if (!this.value) {
      this.value = '';
    }
  }
}

export class TGACheckboxQuestion extends TGAQuestion<{ key: string; selected: boolean }[]> {
  static readonly checkboxControlType = 'checkbox';
  controlType = TGACheckboxQuestion.checkboxControlType;

  constructor(options: {
    key: string;
    label: string;
    value?: { key: string; selected: boolean }[];
    options: { key: string; value: string }[];
    order?: number;
    forCropIds?: number[];
    required?: boolean;
    placeholder?: string;
    isVisible?: boolean;
    hasInfoIcon?: boolean;
    dependsOnQuestion?: QuestionDependency;
    dependsOnExternalProperty?: ExternalPropertyDependency;
  }) {
    super(options);

    this.value ??= this.options?.map((o) => ({ key: o.key, selected: false }));
  }
}

export class TGADropdownQuestion extends TGAQuestion<string> {
  static readonly dropdownControlType = 'dropdown';
  controlType = TGADropdownQuestion.dropdownControlType;

  constructor({
    options,
  }: {
    options: {
      key: string;
      label: string;
      value?: string;
      options: { key: string; value: string }[];
      order?: number;
      forCropIds?: number[];
      required?: boolean;
      placeholder?: string;
      description?: string;
      isVisible?: boolean;
      isDisabled?: boolean;
      dependsOnQuestion?: QuestionDependency;
      dependsOnExternalProperty?: ExternalPropertyDependency;
    };
  }) {
    super(options);

    if (!this.value) {
      this.value = '';
    }
  }
}

export class TGANumberQuestion extends TGAQuestion<number> {
  static readonly numberControlType = 'number';
  range?: { min: number; max: number };
  controlType = TGANumberQuestion.numberControlType;

  constructor(options: {
    key: string;
    label: string;
    value?: number;
    order?: number;
    forCropIds?: number[];
    required?: boolean;
    placeholder?: string;
    isVisible?: boolean;
    dependsOnQuestion?: QuestionDependency;
    dependsOnExternalProperty?: ExternalPropertyDependency;
    range?: { min: number; max: number };
  }) {
    super(options);

    this.range = options.range;
    if (!this.value) {
      this.value = undefined;
    }
  }
}

export class TGADateYearQuestion extends TGAQuestion<number> {
  static readonly numberControlType = 'date-year';
  range: { min: Date; max: Date };
  controlType = TGADateYearQuestion.numberControlType;

  constructor(options: {
    key: string;
    label: string;
    value?: number;
    order?: number;
    forCropIds?: number[];
    required?: boolean;
    placeholder?: string;
    isVisible?: boolean;
    dependsOnQuestion?: QuestionDependency;
    dependsOnExternalProperty?: ExternalPropertyDependency;
    range: { min: Date; max: Date };
  }) {
    super(options);
    this.range = options.range;

    if (!this.value) {
      this.value = undefined;
    }
  }
}

/**
 * This question type is used to show a warning message in the questionnaire instead of a regular question.
 * The value is always undefined, as it does not require user input.
 */
export class TGAWarningMessageQuestion extends TGAQuestion<string> {
  static readonly stringControlType = 'warning-message';
  controlType = TGAWarningMessageQuestion.stringControlType;
  showAsRegularText = false;

  constructor(options: {
    key: string;
    label: string;
    value?: string;
    order?: number;
    forCropIds?: number[];
    required?: boolean;
    placeholder?: string;
    isVisible?: boolean;
    showAsRegularText?: boolean;
    dependsOnQuestion?: QuestionDependency;
    dependsOnExternalProperty?: ExternalPropertyDependency;
  }) {
    super(options);
    this.value = undefined;
    this.showAsRegularText = options.showAsRegularText ?? false;
  }
}

export class TGAFreeTextQuestion extends TGAQuestion<string> {
  static readonly stringControlType = 'free-text';
  controlType = TGAFreeTextQuestion.stringControlType;

  constructor(options: {
    key: string;
    label: string;
    value?: string;
    order?: number;
    forCropIds?: number[];
    required?: boolean;
    placeholder?: string;
    isVisible?: boolean;
    dependsOnQuestion?: QuestionDependency;
    dependsOnExternalProperty?: ExternalPropertyDependency;
  }) {
    super(options);
    this.value = undefined;
  }
}

export class TGATableQuestion extends TGAQuestion<null> {
  static readonly tableControlType = 'table';
  controlType = TGATableQuestion.tableControlType;
  subQuestions: TGAQuestion<TgaInputType>[];

  constructor(options: {
    key: string;
    label: string;
    subQuestions: TGAQuestion<TgaInputType>[];
    order?: number;
    forCropIds?: number[];
    required?: boolean;
    isVisible?: boolean;
    dependsOnQuestion?: QuestionDependency;
    dependsOnExternalProperty?: ExternalPropertyDependency;
  }) {
    super({
      ...options,
      value: null,
      placeholder: undefined,
    });
    this.subQuestions = options.subQuestions;
  }
}

export enum CultivationFrequency {
  In1or3 = 1,
  In1or4 = 2,
  In1or8 = 3,
}

export enum MainSoilTillageMethod {
  TraditioneelPloegBelow = 1,
  TraditioneelPloegAbove = 2,
  SpittenBelow = 3,
  SpittenAbove = 4,
  BovenoverPloegBelow = 5,
  BovenoverPloegAbove = 6,
  NietKerendeGrondbewerking = 7,
  GeenGrondbewerking = 8,
}

export enum Structuurbederfsource {
  No = 1,
  Bewerkbaarheid = 2,
  StorendeLaag = 5,
  Doorlatendheid = 6,
}

export enum DepthStorendeLaag {
  In0to10 = 1,
  In10to25 = 2,
  In25to35 = 3,
  In35orMore = 4,
}

export enum MaterialState {
  Liquid = 1,
  Solid = 2,
  LiquidAndSolid = 3,
}

export enum GrasslandOrGrassSeed {
  No = 1,
  YesLast5Years = 2,
  Yes5To10Years = 3,
  YesMoreThan10Years = 4
}

export enum NematodeLaboraties {
  Eurofins = 'Eurofins',
  HLB = 'HLB',
  NAK = 'NAK',
  Nemacontrol = 'Nemacontrol',
  Roba = 'Roba',
  Other = 'Other'
}
